import { useEffect, useState } from "react";
import { motion, useAnimate, type TargetAndTransition, type Transition } from "framer-motion";
import Confetti from "react-confetti";
import HappyDocMascot from "@/assets/hd-icons/happydoc-mascot-sticker.svg?react";
import type { ScoutState } from "@/tours/types";

type ScoutAvatarBadgeProps = {
    state: ScoutState;
};

const animations: Record<ScoutState, TargetAndTransition> = {
    idle: {
        x: [0, 3, 0, -3, 0],
        rotate: [0, 1.5, 0, -1.5, 0],
    },
    wave: {
        y: [0, -10, 0, -6, 0],
        rotate: [0, 8, -4, 8, 0],
    },
    point: {
        x: [0, 8, 8, 0],
        rotate: [0, 5, 5, 0],
    },
    celebrate: {}, // handled by useAnimate sequence
};

const transitions: Record<ScoutState, Transition> = {
    idle: { duration: 4, ease: "easeInOut", repeat: Infinity },
    wave: { duration: 1.3, ease: "easeInOut" },
    point: { duration: 1.2, ease: "easeInOut" },
    celebrate: {},
};

const CONFETTI_COLORS = ["#2EDEE5", "#FCB718", "#FE2962"];

export default function ScoutAvatarBadge({ state }: ScoutAvatarBadgeProps) {
    const [scope, animate] = useAnimate();
    const [showConfetti, setShowConfetti] = useState(false);

    useEffect(() => {
        if (state !== "celebrate") {
            setShowConfetti(false);
            return;
        }

        setShowConfetti(true);
        const timer = setTimeout(() => setShowConfetti(false), 2000);

        // Two spins + scale bounce (guard against unmount mid-animation)
        const sequence = async () => {
            if (!scope.current) return;
            await animate(scope.current, { rotate: 720, scale: 1.15 }, { duration: 0.7, ease: "easeInOut" });
            if (!scope.current) return;
            await animate(scope.current, { scale: 0.95 }, { duration: 0.15, ease: "easeIn" });
            if (!scope.current) return;
            await animate(scope.current, { scale: 1 }, { duration: 0.2, ease: "easeOut" });
            if (scope.current) animate(scope.current, { rotate: 0 }, { duration: 0 });
        };
        sequence().catch((err) => {
            // Animation cancellation on unmount is expected — only warn on real errors
            if (scope.current) {
                console.warn("[Tours] Scout animation error:", err);
            }
        });

        return () => clearTimeout(timer);
    }, [state, animate, scope]);

    return (
        <div style={{ position: "relative" }}>
            <Confetti
                width={200}
                height={200}
                numberOfPieces={showConfetti ? 60 : 0}
                colors={CONFETTI_COLORS}
                recycle={false}
                gravity={0.3}
                initialVelocityX={4}
                initialVelocityY={8}
                confettiSource={{ x: 50, y: 50, w: 0, h: 0 }}
                style={{
                    position: "absolute",
                    top: -50,
                    left: -50,
                    zIndex: 10,
                    pointerEvents: "none",
                }}
            />
            <motion.div
                ref={scope}
                className="w-[100px] h-[100px] rounded-full bg-white flex-shrink-0 flex items-center justify-center overflow-hidden"
                style={{
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15), 0 0 0 3px white",
                }}
                animate={state !== "celebrate" ? animations[state] : undefined}
                transition={state !== "celebrate" ? transitions[state] : undefined}
            >
                <HappyDocMascot className="w-[130px] h-[130px]" style={{ margin: "-20px" }} />
            </motion.div>
        </div>
    );
}
